import { Link, useLocation } from 'react-router-dom';
import StepIndicator from '../components/ui/StepIndicator.jsx';
import Icon from '../components/ui/Icon.jsx';
import { useApi } from '../hooks/useApi.js';
import { getOrder } from '../api/ordersApi.js';
import { fmtTime } from '../utils/format.js';
import './SuccessPage.css';

// 「接下來會發生什麼」的說明步驟（非真實 timeline——剛下單只會有「訂單已建立」一筆）
const NEXT_STEPS = [
  { done: true,  label: '訂單已送出', time: '剛剛' },
  { done: false, label: '客服聯繫確認', time: '營業時間 1 小時內' },
  { done: false, label: '確認付款方式', time: '轉帳 / 面交 / 貨到付款' },
  { done: false, label: '備貨出貨',   time: '付款確認後 1–2 工作天' },
  { done: false, label: '送達',       time: '依物流時效' },
];

function SuccessPage() {
  // 結帳完成後由 CheckoutPage 帶 orderId 進來；直接打網址進來則沒有
  const orderId = useLocation().state?.orderId;
  const { data: order, loading } = useApi(
    () => (orderId ? getOrder(orderId) : Promise.resolve(null)),
    [orderId],
  );

  if (!orderId) {
    return (
      <main className="success">
        <div className="hero-card">
          <h1 className="success-h1">找不到剛完成的訂單</h1>
          <p className="success-msg">這個頁面只在完成結帳後顯示。要查看歷史訂單請到會員中心。</p>
          <div className="action-row">
            <Link className="action-btn primary" to="/account">前往會員中心 <Icon name="arrow" size={16} /></Link>
          </div>
        </div>
      </main>
    );
  }

  if (loading || !order) {
    return <main className="success"><div className="hero-card"><h1 className="success-h1">載入訂單中…</h1></div></main>;
  }

  return (
    <main className="success">
      <StepIndicator current={4} align="center" />

      <div className="hero-card">
        <div className="check-circle">
          <svg width="44" height="44">
            <use href="#i-check" stroke="currentColor" strokeWidth="80" />
          </svg>
        </div>
        <h1 className="success-h1">
          訂單已送出！
          <small>ORDER RECEIVED · 客服將聯繫你</small>
        </h1>
        <p className="success-msg">
          感謝您在 MICHIDART 下單。我們已收到您的訂單，客服會在確認訂單後透過您選擇的方式聯繫您，確認商品、金額與付款方式（轉帳 ），本步驟不會線上扣款。
        </p>
        <dl className="order-meta">
          <div><dt>訂單編號</dt><dd>{order.id}</dd></div>
          <div><dt>下單時間</dt><dd>{fmtTime(order.createdAt)}</dd></div>
          <div><dt>結帳方式</dt><dd>{order.contactMethodLabel || '專人聯繫'}</dd></div>
          <div><dt>訂單金額</dt><dd style={{ color: 'var(--red)' }}>NT${(order.total ?? 0).toLocaleString()}</dd></div>
          {order.pointsRedeemed > 0 && (
            <div><dt>點數折抵</dt><dd>−{order.pointsRedeemed.toLocaleString()} P</dd></div>
          )}
          {(() => {
            // 回饋於「已送達」時發放；這裡先預估給客人看（商品小計−折抵 的 5%）
            const base = Math.max(0, (order.subtotal ?? 0) - (order.pointsRedeemed ?? 0));
            const est = Math.floor(base * 0.05);
            return est > 0
              ? <div><dt>預計回饋</dt><dd style={{ color: 'var(--mint-deep)' }}>+{est.toLocaleString()} P<small style={{ color: 'var(--ink-soft)', fontWeight: 400 }}>（送達後發放）</small></dd></div>
              : null;
          })()}
        </dl>
      </div>

      <div className="detail-grid">
        <div className="detail-section">
          <h3>訂單進度</h3>
          {NEXT_STEPS.map((t, i) => (
            <div className="tl-item" key={i}>
              <div className={`tl-dot${t.done ? ' done' : ''}`}>{i + 1}</div>
              <div><b>{t.label}</b><small>{t.time}</small></div>
            </div>
          ))}
        </div>
        <div>
          <div className="detail-section" style={{ marginBottom: 18 }}>
            <h3>配送資訊</h3>
            <div style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--ink-strong)' }}>
              <div><b>{order.recipientName}</b></div>
              <div>{order.recipientPhone}</div>
              <div>{order.recipientAddress}</div>
              {order.shippingMethodLabel && <div style={{ color: 'var(--ink-soft)' }}>{order.shippingMethodLabel}</div>}
            </div>
          </div>
          <div className="detail-section">
            <h3>訂購商品</h3>
            {(order.items || []).map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--line)', fontSize: 13 }}>
                <span>{item.name}{item.spec ? `（${item.spec}）` : ''} ×{item.qty}</span>
                <b style={{ color: 'var(--navy)' }}>NT${((item.unitPrice ?? 0) * (item.qty ?? 1)).toLocaleString()}</b>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, fontWeight: 800, color: 'var(--navy)' }}>
              <span>合計{order.shipping > 0 ? `（含運費 NT$${order.shipping}）` : '（免運）'}</span>
              <span style={{ color: 'var(--red)', fontSize: 20 }}>NT${(order.total ?? 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="action-row">
        <Link className="action-btn ghost" to="/shop">
          <Icon name="arrow-l" size={16} /> 繼續購物
        </Link>
        <Link className="action-btn primary" to={`/account/orders/${order.id}`}>
          查看訂單 <Icon name="arrow" size={16} />
        </Link>
      </div>
    </main>
  );
}

export default SuccessPage;
